import { useQueryClient } from '@tanstack/react-query'
import { $api } from './queryClient'
import type { components } from './schema'

/**
 * The dated rent of a lease and its review formula (spec 2026-10-25-07). The
 * due dates and the prorated first period come from the server preview: the
 * front carries no copy of the calculation.
 */

export type LeaseRentTerm = components['schemas']['LeaseRentTerm']
export type LeaseRentTermInput = components['schemas']['LeaseRentTermInput']
export type LeaseRentTerms = components['schemas']['LeaseRentTerms']
export type LeaseRentPackDefault = components['schemas']['LeaseRentPackDefault']
export type DueRule = components['schemas']['DueRule']
export type RentReviewTerms = components['schemas']['RentReviewTerms']
export type RentIndex = components['schemas']['RentIndex']

const TERMS = '/api/v1/organizations/{orgCode}/contracts/{contractUid}/rent-terms' as const
const REVIEW = '/api/v1/organizations/{orgCode}/contracts/{contractUid}/rent-review-terms' as const

function useInvalidateRent() {
  const queryClient = useQueryClient()
  return () =>
    queryClient.invalidateQueries({
      predicate: (q) => {
        const path = (q.queryKey as [string, string])[1]
        return typeof path === 'string' && (path.includes('/rent-terms') || path.includes('/rent-review-terms'))
      },
    })
}

export function useRentTerms(orgCode: string, contractUid: string) {
  return $api.useQuery('get', TERMS, { params: { path: { orgCode, contractUid } } })
}

export function useRentTermPreview(orgCode: string, contractUid: string, query: Record<string, string | number | undefined> | null) {
  return $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/contracts/{contractUid}/rent-terms/preview',
    { params: { path: { orgCode, contractUid }, query: (query ?? {}) as never } },
    { enabled: query !== null, retry: false },
  )
}

export function useCreateRentTerm() {
  const invalidate = useInvalidateRent()
  return $api.useMutation('post', TERMS, { onSuccess: invalidate })
}

export function useUpdateRentTerm() {
  const invalidate = useInvalidateRent()
  return $api.useMutation('patch', `${TERMS}/{termUid}` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/rent-terms/{termUid}', { onSuccess: invalidate })
}

export function useDeleteRentTerm() {
  const invalidate = useInvalidateRent()
  return $api.useMutation('delete', `${TERMS}/{termUid}` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/rent-terms/{termUid}', { onSuccess: invalidate })
}

export function useRentReviewTerms(orgCode: string, contractUid: string) {
  return $api.useQuery('get', REVIEW, { params: { path: { orgCode, contractUid } } })
}

export function usePutRentReviewTerms() {
  const invalidate = useInvalidateRent()
  return $api.useMutation('put', REVIEW, { onSuccess: invalidate })
}

export function useRentIndices(orgCode: string, filter: { jurisdiction?: string; index_code?: string } = {}) {
  return $api.useQuery('get', '/api/v1/organizations/{orgCode}/rent-indices', {
    params: { path: { orgCode }, query: filter },
  })
}

/** The platform admin's table and writes. */
export function useAdminRentIndices(filter: { jurisdiction?: string; index_code?: string } = {}) {
  return $api.useQuery('get', '/api/v1/admin/rent-indices', { params: { query: filter } })
}

function useInvalidateIndices() {
  const queryClient = useQueryClient()
  return () =>
    queryClient.invalidateQueries({
      predicate: (q) => {
        const path = (q.queryKey as [string, string])[1]
        return typeof path === 'string' && path.includes('rent-indices')
      },
    })
}

export function useCreateRentIndex() {
  const invalidate = useInvalidateIndices()
  return $api.useMutation('post', '/api/v1/admin/rent-indices', { onSuccess: invalidate })
}

export function useRentIndexImportStatus() {
  return $api.useQuery('get', '/api/v1/admin/rent-indices/import-status', {}, { refetchInterval: 5000 })
}

export function useRunRentIndexImport() {
  const invalidate = useInvalidateIndices()
  return $api.useMutation('post', '/api/v1/admin/rent-indices/import', { onSuccess: invalidate })
}

/** Rent reviews and lease guarantees (spec 2026-10-25-08). The engine computes on the server; the front only shows it. */
export type RentReviews = components['schemas']['RentReviews']
export type RentReviewOutcome = components['schemas']['RentReviewOutcome']
export type RentReviewProposal = components['schemas']['RentReviewProposal']
export type RentReviewInput = components['schemas']['RentReviewInput']
export type LeaseGuarantees = components['schemas']['LeaseGuarantees']
export type GuaranteeConflict = components['schemas']['GuaranteeConflict']

const REVIEWS = '/api/v1/organizations/{orgCode}/contracts/{contractUid}/rent-reviews' as const
const GUARANTEES = '/api/v1/organizations/{orgCode}/contracts/{contractUid}/guarantees' as const

function useInvalidateLease() {
  const queryClient = useQueryClient()
  return () =>
    queryClient.invalidateQueries({
      predicate: (q) => {
        const path = (q.queryKey as [string, string])[1]
        return typeof path === 'string' && (path.includes('/rent-') || path.includes('/guarantees') || path.includes('/stakeholders') || path.includes('/documents') || path.includes('/properties'))
      },
    })
}

export function useRentReviews(orgCode: string, contractUid: string) {
  return $api.useQuery('get', REVIEWS, { params: { path: { orgCode, contractUid } } })
}

export function useCreateRentReview() {
  const invalidate = useInvalidateLease()
  return $api.useMutation('post', REVIEWS, { onSuccess: invalidate })
}

export function useAcceptRentReview() {
  const invalidate = useInvalidateLease()
  return $api.useMutation('post', `${REVIEWS}/{proposalUid}/accept` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/rent-reviews/{proposalUid}/accept', { onSuccess: invalidate })
}

export function useRejectRentReview() {
  const invalidate = useInvalidateLease()
  return $api.useMutation('post', `${REVIEWS}/{proposalUid}/reject` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/rent-reviews/{proposalUid}/reject', { onSuccess: invalidate })
}

export function useLeaseGuarantees(orgCode: string, contractUid: string) {
  return $api.useQuery('get', GUARANTEES, { params: { path: { orgCode, contractUid } } })
}

export function useCreateLeaseGuarantee() {
  const invalidate = useInvalidateLease()
  return $api.useMutation('post', GUARANTEES, { onSuccess: invalidate })
}

/** The rules a 409 guarantee_conflict names, or null for any other error. */
export function guaranteeConflicts(err: unknown): GuaranteeConflict[] | null {
  const e = err as { code?: string; conflicts?: GuaranteeConflict[] } | null
  return e && e.code === 'guarantee_conflict' ? (e.conflicts ?? []) : null
}
